"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CarouselControls from "../carousel/carousel-controls";
import OverlayCard from "../card/overlay-card";
import { Item, GalleryProps } from "@/types/item";
import { useResponsiveCarousel } from "@/hooks/useResponsiveCarousel";
import PreviewCard from "../card/preview-card";
import SectionTitle from "../SectionTitle";
import PageSection from "@/components/shared/page-section";

const CARD_WIDTH = 366;
const GAP = 16;
const MAX_PER_PAGE = 3;

export default function PreviewGallery(props: GalleryProps) {
	const {
		title1,
		title2,
		services,
		leftIcon,
		rightIcon,
		hideIconOnMobile,
		isAdmin = false,
		isNews = false,
		noTopPadding = false,
		hideTitle = false,
		manageContent = false,
		contentEntity,
		bgClass,
	} = props;

	const [selectedService, setSelectedService] = useState<Item | null>(null);
	const [items, setItems] = useState<Item[]>(services);

	useEffect(() => {
		setItems(services);
	}, [services]);

	const {
		wrapperRef,
		page,
		setPage,
		perPage,
		pageCount,
		cardWidth,
		trackWidth,
		handleTouchStart,
		handleTouchMove,
		handleTouchEnd,
	} = useResponsiveCarousel({
		cardWidth: CARD_WIDTH,
		gap: GAP,
		maxPerPage: MAX_PER_PAGE,
		itemCount: items.length,
	});

	const startIndex = page * perPage;
	const visibleItems = items.slice(startIndex, startIndex + perPage);

	return (
		<PageSection className={`${noTopPadding ? "pt-0" : ""} ${bgClass ?? ""}`}>
			{!hideTitle && (
				<SectionTitle
					title1={title1}
					title2={title2}
					leftIcon={leftIcon}
					rightIcon={rightIcon}
					hideIconOnMobile={hideIconOnMobile}
				/>
			)}

			<div ref={wrapperRef} className="relative mx-auto pb-12 w-full min-w-0 mt-6">
				<div
					className="overflow-hidden touch-pan-x"
					onTouchStart={handleTouchStart}
					onTouchMove={handleTouchMove}
					onTouchEnd={handleTouchEnd}
				>
					<AnimatePresence mode="wait">
						<motion.div
							key={`${page}-${perPage}`}
							className="flex gap-4 justify-center mx-auto"
							style={{ maxWidth: trackWidth }}
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.95 }}
							transition={{ duration: 0.25, ease: "easeOut" }}
						>
							{visibleItems.map((service) => (
								<div
									key={service.id}
									className="flex-shrink-0 w-full max-w-[366px]"
									style={{ width: cardWidth }}
								>
									<PreviewCard
										id={service.id}
										title={service.title}
										description={service.description}
										image={service.image}
										previewImage={service.previewImage}
										isNews={isNews}
										onClick={() => setSelectedService(service)}
									/>
								</div>
							))}
						</motion.div>
					</AnimatePresence>
				</div>

				{pageCount > 1 && (
					<div className="absolute bottom-0 left-1/2 -translate-x-1/2 mt-4">
						<CarouselControls
							total={pageCount}
							activeIndex={page}
							onPrev={() => setPage((prev) => Math.max(0, prev - 1))}
							onNext={() =>
								setPage((prev) => Math.min(pageCount - 1, prev + 1))
							}
							disablePrev={page === 0}
							disableNext={page === pageCount - 1}
							onDotClick={(index) => setPage(index)}
						/>
					</div>
				)}
			</div>

			<OverlayCard
				selectedItem={selectedService}
				onClose={() => setSelectedService(null)}
				isAdmin={isAdmin}
				isNews={isNews}
				manageContent={manageContent}
				contentEntity={contentEntity}
				onDelete={(id) =>
					setItems((prev) => prev.filter((item) => item.id !== id))
				}
			/>
		</PageSection>
	);
}
